import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import ListSearch, { filterBy } from "@/components/ListSearch";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Mail, Phone, MapPin, Users, Truck, FileText, Sparkles } from "lucide-react";
import StatementDialog from "@/components/StatementDialog";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import UpgradeNudge from "@/components/UpgradeNudge";
import BulkActionBar from "@/components/BulkActionBar";
import TidyUpDialog from "@/components/TidyUpDialog";
import { toast } from "sonner";

const BLANK = { name: "", email: "", phone: "", address: "", tax_id: "", notes: "", default_invoice_notes: "" };

export function PartyList({ kind = "customer" }) {
    const { currentOrg } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [q, setQ] = useState("");
    const [statementFor, setStatementFor] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [tidyOpen, setTidyOpen] = useState(false);
    const path = kind === "customer" ? "/customers" : "/vendors";

    const load = async () => {
        const { data } = await api.get(path);
        setItems(data.items || []);
    };
    useEffect(() => { if (currentOrg) load(); }, [currentOrg, kind]);

    const openCreate = () => {
        setEditing(null);
        setForm(BLANK);
        setOpen(true);
    };

    const openEdit = (c) => {
        setEditing(c);
        setForm({
            name: c.name || "",
            email: c.email || "",
            phone: c.phone || "",
            address: c.address || "",
            tax_id: c.tax_id || "",
            notes: c.notes || "",
            default_invoice_notes: c.default_invoice_notes || "",
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.name) return toast.error("Name required");
        const payload = { ...form, email: form.email || null };
        try {
            if (editing) {
                await api.patch(`${path}/${editing.id}`, payload);
                toast.success(`${kind === "customer" ? "Customer" : "Vendor"} updated`);
            } else {
                await api.post(path, payload);
                toast.success(`${kind === "customer" ? "Customer" : "Vendor"} added`);
            }
            setOpen(false);
            setForm(BLANK);
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete?")) return;
        try { await api.delete(`${path}/${id}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleSel = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const bulkDelete = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        const label = kind === "customer" ? "customer" : "vendor";
        if (!window.confirm(`Delete ${ids.length} ${label}${ids.length === 1 ? "" : "s"}?\n\nThis action cannot be undone.`)) return;
        setBulkBusy(true);
        const results = await Promise.allSettled(ids.map((id) => api.delete(`${path}/${id}`)));
        const failed = results.filter((r) => r.status === "rejected").length;
        const ok = results.length - failed;
        if (ok) toast.success(`${ok} ${label}${ok === 1 ? "" : "s"} deleted`);
        if (failed) toast.error(`${failed} could not be deleted`);
        setSelected(new Set());
        setBulkBusy(false);
        load();
    };

    const Icon = kind === "customer" ? Users : Truck;
    const title = kind === "customer" ? "Customers" : "Vendors";

    return (
        <div>
            <PageHeader
                subtitle={kind === "customer" ? "Sales" : "Purchases"}
                title={title}
                action={
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setTidyOpen(true)}
                            className="rounded-xl border-secondary/40 text-secondary-dark hover:bg-secondary/10"
                            data-testid={`${kind}-tidy-up-btn`}
                            title={`Find and merge duplicate ${kind}s`}
                        >
                            <Sparkles className="h-4 w-4 mr-1.5" /> Tidy up
                        </Button>
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(BLANK); } }}>
                            <DialogTrigger asChild>
                                <Button onClick={openCreate} className="bg-ocean-700 text-white rounded-xl" data-testid={`new-${kind}-btn`}>
                                    <Plus className="h-4 w-4 mr-1.5" /> Add {kind}
                                </Button>
                            </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? `Edit ${kind}` : `Add ${kind}`}</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${kind}-name-input`} /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Email</Label><Input className="mt-1.5" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                                    <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                                </div>
                                <div><Label>Address</Label><Input className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                                <div><Label>Tax ID</Label><Input className="mt-1.5" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} /></div>
                                {kind === "customer" && (
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <Label>Default invoice notes</Label>
                                            <span className="text-[11px] text-muted-foreground">
                                                Auto-fills new invoices for this customer · {(form.default_invoice_notes || "").length}/1000
                                            </span>
                                        </div>
                                        <Textarea
                                            rows={3}
                                            maxLength={1000}
                                            value={form.default_invoice_notes || ""}
                                            onChange={(e) => setForm({ ...form, default_invoice_notes: e.target.value })}
                                            placeholder="e.g. Net-30 · Wire transfers only · Reference PO number on payment"
                                            className="mt-1.5"
                                            data-testid="customer-default-notes-input"
                                        />
                                    </div>
                                )}
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button onClick={submit} className="bg-ocean-700 text-white" data-testid={`submit-${kind}-btn`}>{editing ? "Save" : "Add"}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                    </div>
                }
            />
            {items.length === 0 ? (
                <EmptyState icon={Icon} title={`No ${kind}s yet`} description={`Add your first ${kind} to start tracking ${kind === "customer" ? "sales" : "purchases"}.`} />
            ) : (
                <>
                    <UpgradeNudge resource={kind === "customer" ? "customers" : "vendors"} />
                    <BulkActionBar
                        count={selected.size}
                        label={kind}
                        onDelete={bulkDelete}
                        onClear={() => setSelected(new Set())}
                        busy={bulkBusy}
                        testId={`${kind}-bulk`}
                    />
                    <div className="mb-4 flex items-center gap-3">
                        <ListSearch value={q} onChange={setQ} placeholder={`Search ${kind}s by name, email, phone or address…`} testId={`${kind}s-search`} />
                        {q && <span className="text-xs text-muted-foreground">{filterBy(items, q, ["name", "email", "phone", "address", "tax_id"]).length} of {items.length}</span>}
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filterBy(items, q, ["name", "email", "phone", "address", "tax_id"]).map((c) => (
                        <div key={c.id} className={`relative rounded-3xl bg-white border p-5 shadow-card hover:shadow-card-hover transition-all ${selected.has(c.id) ? "border-secondary ring-2 ring-secondary/30" : "border-sand-200"}`} data-testid={`${kind}-card-${c.id}`}>
                            <input
                                type="checkbox"
                                checked={selected.has(c.id)}
                                onChange={() => toggleSel(c.id)}
                                className="absolute top-4 left-4 h-4 w-4 accent-secondary cursor-pointer"
                                data-testid={`${kind}-select-${c.id}`}
                                aria-label={`Select ${c.name}`}
                            />
                            <div className="flex items-center justify-between pl-7">
                                <div className="flex items-center gap-3">
                                    <div className="h-11 w-11 rounded-xl bg-ocean-50 text-secondary grid place-items-center font-heading font-medium">
                                        {c.name.slice(0, 1).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-medium text-ocean-700">{c.name}</div>
                                        {c.tax_id && <div className="text-xs text-muted-foreground">{c.tax_id}</div>}
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    {kind === "customer" && (
                                        <Button variant="ghost" size="icon" onClick={() => setStatementFor(c)} data-testid={`statement-${c.id}`} title="Send statement">
                                            <FileText className="h-4 w-4 text-muted-foreground" />
                                        </Button>
                                    )}
                                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-${kind}-${c.id}`}><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
                                    <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`delete-${kind}-${c.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                                </div>
                            </div>
                            <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                                {c.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {c.email}</div>}
                                {c.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {c.phone}</div>}
                                {c.address && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {c.address}</div>}
                            </div>
                        </div>
                        ))}
                    </div>
                </>
            )}
            <StatementDialog customer={statementFor} onClose={() => setStatementFor(null)} />
            <TidyUpDialog
                open={tidyOpen}
                onOpenChange={setTidyOpen}
                label={kind}
                fetchUrl={`${path}/duplicate-groups`}
                mergeUrl={`${path}/merge`}
                onDone={load}
                renderRow={(m, isCanonical) => (
                    <div>
                        <div className={`font-medium text-sm truncate ${isCanonical ? "text-secondary-dark" : "text-ocean-700"}`}>{m.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                            {m.email && <span>{m.email}</span>}{m.email && m.phone && " · "}{m.phone && <span>{m.phone}</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            {(m.usage?.invoices || m.usage?.bills) ? (
                                <span>Used on {m.usage.invoices || 0} invoice{m.usage.invoices === 1 ? "" : "s"}, {m.usage.bills || 0} bill{m.usage.bills === 1 ? "" : "s"}</span>
                            ) : <span>No activity yet</span>}
                        </div>
                    </div>
                )}
            />
        </div>
    );
}

export default function Customers() {
    return <PartyList kind="customer" />;
}
